/**
 * Employee Portal API Client
 *
 * Uses the shared API base factory with Employee SSO auth tokens.
 */

import { createApiClient } from '@shared/services/apiBase';
import { authService } from './auth';
import type {
  User,
  Project,
  ProjectBranding,
  ProjectCreate,
  ProjectUpdate,
  DashboardMetrics,
  Customer,
  CustomerCreate,
  Invitation,
  InvitationCreate,
  FileUpload,
  AppConfig,
  BrandingConfig,
  SSOConfig,
  DownloadLogResponse,
  PortalUrls,
  Platform,
  PlatformCreate,
  SecurityOverview,
  ComplianceStatus,
} from '@shared/types';

const client = createApiClient({
  getToken: () => authService.getIdToken(),
  onUnauthorized: () => authService.logout(),
});

export const apiClient = {
  // =========================================================================
  // User Endpoints
  // =========================================================================

  async getCurrentUser(): Promise<User> {
    const response = await client.get('/users/me');
    return response.data;
  },

  async getUsers(): Promise<User[]> {
    const response = await client.get('/users');
    return response.data;
  },

  async getUser(id: string): Promise<User> {
    const response = await client.get(`/users/${id}`);
    return response.data;
  },

  async syncUsersPreview(): Promise<{ to_add: any[]; to_update: any[]; to_deactivate: any[] }> {
    const response = await client.post('/admin/users/sync/preview');
    return response.data;
  },

  async syncUsers(): Promise<{ added: number; updated: number; deactivated: number }> {
    const response = await client.post('/admin/users/sync');
    return response.data;
  },

  // =========================================================================
  // Project Endpoints
  // =========================================================================

  async getProjects(): Promise<Project[]> {
    const response = await client.get('/projects');
    return response.data;
  },

  async getProjectMetrics(): Promise<DashboardMetrics> {
    const response = await client.get('/projects/metrics');
    return response.data;
  },

  async getProject(id: string): Promise<Project> {
    const response = await client.get(`/projects/${id}`);
    return response.data;
  },

  async createProject(data: ProjectCreate): Promise<Project> {
    const response = await client.post('/projects', data);
    return response.data;
  },

  async updateProject(id: string, data: ProjectUpdate): Promise<Project> {
    const response = await client.patch(`/projects/${id}`, data);
    return response.data;
  },

  async deleteProject(id: string): Promise<void> {
    await client.delete(`/projects/${id}`);
  },

  async retryProjectStorage(projectId: string): Promise<Project> {
    const response = await client.post(`/projects/${projectId}/provision-storage`);
    return response.data;
  },

  // =========================================================================
  // Project Branding Endpoints
  // =========================================================================

  async getBrandingUploadUrl(
    projectId: string,
    assetType: string,
    contentType: string,
  ): Promise<{ upload_url: string; s3_key: string }> {
    const response = await client.post(`/projects/${projectId}/branding/upload-url`, {
      asset_type: assetType,
      content_type: contentType,
    });
    return response.data;
  },

  async updateProjectBranding(
    projectId: string,
    updates: Partial<ProjectBranding & { logo_light_key?: string; logo_dark_key?: string; logo_mark_light_key?: string; logo_mark_dark_key?: string; favicon_key?: string }>,
  ): Promise<ProjectBranding> {
    const response = await client.patch(`/projects/${projectId}/branding`, updates);
    return response.data;
  },

  async deleteProjectBrandingAsset(
    projectId: string,
    assetType: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await client.delete(`/projects/${projectId}/branding/${assetType}`);
    return response.data;
  },

  // =========================================================================
  // Customer Endpoints (per project)
  // =========================================================================

  async getProjectCustomers(projectId: string): Promise<Customer[]> {
    const response = await client.get(`/projects/${projectId}/customers`);
    return response.data;
  },

  async addProjectCustomer(projectId: string, data: CustomerCreate): Promise<Customer> {
    const response = await client.post(`/projects/${projectId}/customers`, data);
    return response.data;
  },

  async removeProjectCustomer(projectId: string, customerId: string): Promise<void> {
    await client.delete(`/projects/${projectId}/customers/${customerId}`);
  },

  // =========================================================================
  // Invitation Endpoints (per project)
  // =========================================================================

  async getProjectInvitations(projectId: string): Promise<Invitation[]> {
    const response = await client.get(`/projects/${projectId}/invitations`);
    return response.data;
  },

  async createProjectInvitation(projectId: string, data: InvitationCreate): Promise<Invitation> {
    const response = await client.post(`/projects/${projectId}/invitations`, data);
    return response.data;
  },

  async updateInvitationStatus(projectId: string, invitationId: string, status: string): Promise<Invitation> {
    const response = await client.patch(`/projects/${projectId}/invitations/${invitationId}`, { status });
    return response.data;
  },

  // =========================================================================
  // Upload Endpoints (per project)
  // =========================================================================

  async getProjectUploads(projectId: string): Promise<FileUpload[]> {
    const response = await client.get(`/projects/${projectId}/uploads`);
    return response.data;
  },

  async cleanupStaleUploads(projectId: string): Promise<{ success: boolean; marked_failed: number }> {
    const response = await client.post(`/projects/${projectId}/uploads/cleanup-stale`);
    return response.data;
  },

  // =========================================================================
  // Configuration Endpoints
  // =========================================================================

  async getPublicConfig(): Promise<BrandingConfig> {
    const response = await client.get('/config/public');
    return response.data;
  },

  async getPortalUrls(): Promise<PortalUrls> {
    const response = await client.get('/config/portal-urls');
    return response.data;
  },

  async getConfig(): Promise<AppConfig> {
    const response = await client.get('/admin/config');
    return response.data;
  },

  async updateConfig(updates: Partial<AppConfig>): Promise<AppConfig> {
    const response = await client.patch('/admin/config', updates);
    return response.data;
  },

  // =========================================================================
  // Platform Configuration
  // =========================================================================

  async getPlatforms(): Promise<Platform[]> {
    const response = await client.get('/config/platforms');
    return response.data;
  },

  async createPlatform(data: PlatformCreate): Promise<Platform> {
    const response = await client.post('/admin/platforms', data);
    return response.data;
  },

  async updatePlatform(id: string, data: PlatformCreate): Promise<Platform> {
    const response = await client.put(`/admin/platforms/${id}`, data);
    return response.data;
  },

  async deletePlatform(id: string): Promise<void> {
    await client.delete(`/admin/platforms/${id}`);
  },

  // =========================================================================
  // Integration Settings
  // =========================================================================

  async getIntegrations(): Promise<Record<string, Record<string, string>>> {
    const response = await client.get('/admin/integrations');
    return response.data;
  },

  async updateIntegrations(updates: Record<string, Record<string, string>>): Promise<Record<string, Record<string, string>>> {
    const response = await client.put('/admin/integrations', updates);
    return response.data;
  },

  // =========================================================================
  // SSO Configuration Endpoints
  // =========================================================================

  async getSsoStatus(): Promise<{
    active_provider: string | null;
    cognito_entity_id: string;
    cognito_acs_url: string;
    providers: Record<string, { configured: boolean; enabled: boolean; config?: Record<string, string> }>;
  }> {
    const response = await client.get('/admin/sso/status');
    return response.data;
  },

  async getSsoConfigs(): Promise<SSOConfig[]> {
    const response = await client.get('/admin/sso');
    return response.data;
  },

  async configureMsEntra(config: {
    tenantId: string;
    clientId: string;
    clientSecret?: string;
    samlMetadataUrl?: string;
  }): Promise<{ success: boolean; message: string }> {
    const payload = {
      tenant_id: config.tenantId,
      client_id: config.clientId,
      client_secret: config.clientSecret,
      saml_metadata_url: config.samlMetadataUrl,
    };
    const response = await client.put('/admin/sso/microsoft-entra', payload);
    return response.data;
  },

  async configureGoogleWorkspace(config: {
    clientId?: string;
    clientSecret?: string;
    domain: string;
    samlMetadataUrl?: string;
    serviceAccountJson?: string;
    adminEmail?: string;
  }): Promise<{ success: boolean; message: string }> {
    const payload = {
      domain: config.domain,
      saml_metadata_url: config.samlMetadataUrl,
      service_account_json: config.serviceAccountJson,
      admin_email: config.adminEmail,
    };
    const response = await client.put('/admin/sso/google-workspace', payload);
    return response.data;
  },

  async activateSso(provider: string): Promise<{ success: boolean; message: string }> {
    const response = await client.post('/admin/sso/activate', { provider });
    return response.data;
  },

  async deactivateSso(): Promise<{ success: boolean; message: string }> {
    const response = await client.post('/admin/sso/deactivate');
    return response.data;
  },

  async fetchEntraGroups(config: {
    tenantId: string;
    clientId: string;
    clientSecret: string;
  }): Promise<{ groups: Array<{ id: string; name: string; description: string | null }> }> {
    const payload = {
      tenant_id: config.tenantId,
      client_id: config.clientId,
      client_secret: config.clientSecret,
    };
    const response = await client.post('/admin/sso/microsoft-entra/groups', payload);
    return response.data;
  },

  async fetchGoogleGroups(config: {
    serviceAccountJson: string;
    domain: string;
    adminEmail: string;
  }): Promise<{ groups: Array<{ id: string; name: string; email?: string; description: string | null }> }> {
    const payload = {
      service_account_json: config.serviceAccountJson,
      domain: config.domain,
      admin_email: config.adminEmail,
    };
    const response = await client.post('/admin/sso/google-workspace/groups', payload);
    return response.data;
  },

  async getRoleMappings(provider: string): Promise<{
    admin_group_ids: string[];
    employee_group_ids: string[];
    leadership_group_ids: string[];
    cached_groups: { id: string; name: string; description: string | null }[];
  }> {
    const response = await client.get(`/admin/sso/${provider}/role-mappings`);
    return response.data;
  },

  async updateRoleMappings(
    provider: string,
    mappings: {
      admin_group_ids: string[];
      employee_group_ids: string[];
      leadership_group_ids: string[];
      cached_groups?: { id: string; name: string; description: string | null }[];
    }
  ): Promise<{ success: boolean; message: string }> {
    const response = await client.put(`/admin/sso/${provider}/role-mappings`, mappings);
    return response.data;
  },

  // =========================================================================
  // Download Access (Admin)
  // =========================================================================

  async toggleDownloadAccess(userId: string, downloadAllowed: boolean): Promise<User> {
    const response = await client.patch(`/admin/users/${userId}/download-access`, { download_allowed: downloadAllowed });
    return response.data;
  },

  async toggleDeleteProjectsAccess(userId: string, deleteProjectsAllowed: boolean): Promise<User> {
    const response = await client.patch(`/admin/users/${userId}/delete-projects-access`, { delete_projects_allowed: deleteProjectsAllowed });
    return response.data;
  },

  // =========================================================================
  // Download Log (Admin)
  // =========================================================================

  async getDownloadLog(params?: { project_id?: string; user_id?: string; limit?: number; cursor?: string }): Promise<DownloadLogResponse> {
    const response = await client.get('/admin/download-log', { params });
    return response.data;
  },

  // =========================================================================
  // Download URL (Employee)
  // =========================================================================

  async getDownloadUrl(projectId: string, uploadSk: string): Promise<{ url: string; expires_in: number; file_name: string }> {
    const response = await client.post(`/projects/${projectId}/files/${encodeURIComponent(uploadSk)}/download-url`);
    return response.data;
  },

  // =========================================================================
  // Project Files (Completed Uploads)
  // =========================================================================

  async getProjectFiles(projectId: string): Promise<FileUpload[]> {
    const response = await client.get(`/projects/${projectId}/files`);
    return response.data;
  },

  // =========================================================================
  // Storage Dashboard (Admin)
  // =========================================================================

  async getStorageOverview(): Promise<{
    buckets: Array<{
      project_id: string;
      project_name: string;
      project_status: string;
      bucket_name: string;
      size_bytes: number;
      object_count: number;
      standard_bytes: number;
      glacier_bytes: number;
      storage_class: string;
      lifecycle_state: string;
      estimated_monthly_cost: number;
    }>;
    totals: {
      total_size_bytes: number;
      total_objects: number;
      total_estimated_monthly_cost: number;
      active_buckets: number;
      expiring_buckets: number;
    };
  }> {
    const response = await client.get('/admin/storage/overview');
    return response.data;
  },

  async getStorageBilling(): Promise<{
    monthly_costs: Array<{ month: string; cost: number }>;
    error?: string;
  }> {
    const response = await client.get('/admin/storage/billing');
    return response.data;
  },

  // Security Dashboard
  async getSecurityOverview(): Promise<SecurityOverview> {
    const response = await client.get('/admin/security/overview');
    return response.data;
  },

  // Compliance Verification
  async getComplianceStatus(): Promise<ComplianceStatus> {
    const response = await client.get('/admin/compliance/status');
    return response.data;
  },

  async runComplianceCheck(): Promise<ComplianceStatus> {
    const response = await client.post('/admin/compliance/check');
    return response.data;
  },

  // Email Previews (Notifications)
  async getEmailPreviews(): Promise<Array<{
    id: string;
    name: string;
    description: string;
    trigger: string;
    recipients: string;
    subject: string;
    html: string;
  }>> {
    const response = await client.get('/notifications/email-previews');
    return response.data;
  },
};
